/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { memo, useCallback } from 'react';
import {
	useIsStreamingEnabled,
	useIsStreamingAdvisoryEnabled,
} from '@jira/ssr__streaming-enabled/src/useIsStreamingEnabled.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import PerformanceMark from '@atlassian/jira-common-performance/src/set-performance-mark.tsx';
import {
	PAGE_LAYOUT_MARK,
	PAGE_CONTAINER_END_MARK,
	GLOBAL_CONTAINERS_END_MARK,
} from '@jira/platform__page-container-constants/src/index.tsx';
import { setInitialPageLoadTimingFromPerformanceMarks } from '@atlassian/jira-spa-performance-breakdown/src/utils/performance-marks-tools/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useFaviconService } from '@atlassian/jira-favicon/src/index.tsx';
import { ForgeUiGlobalExtensionsAsync } from '@jira/forge__forge-ui-global-extensions/src/ui/async.tsx';
import { PageContainer } from '../../common/ui/page-container/main.tsx';
import { Content } from './content/index.tsx';
import type { SpaProps } from './types.tsx';
import { ReadinessTracker } from './readiness-tracker.tsx';
import { HotfixTesting } from './hotfix-testing.tsx';
import { HydrationAdvisory } from './HydrationAdvisory.tsx';
import { StreamingAdvisory } from './StreamingAdvisory.tsx';

const FaviconService = memo(() => {
	useFaviconService();

	return null;
});

export const Spa = ({
	locale,
	messages,
	Router,
	routes,
	routerLinkComponent,
	initRoute,
}: SpaProps): React.JSX.Element => {
	const isStreamingEnabled = useIsStreamingEnabled();
	const isStreamingAdvisoryEnabled = useIsStreamingAdvisoryEnabled();

	const pageContainerPerformanceTiming = useCallback(() => {
		setInitialPageLoadTimingFromPerformanceMarks(
			PAGE_LAYOUT_MARK,
			GLOBAL_CONTAINERS_END_MARK,
			PAGE_CONTAINER_END_MARK,
			true,
			true,
		);
	}, []);

	const content = (
		<PageContainer
			id={__SERVER__ ? 'jira.entry.ssr' : 'spa'}
			locale={locale}
			messages={messages}
			Router={Router}
			routerLinkComponent={routerLinkComponent}
		>
			<ReadinessTracker />
			<FaviconService />
			<PerformanceMark metricKey={PAGE_CONTAINER_END_MARK} onSet={pageContainerPerformanceTiming} />
			<Content initRoute={initRoute} routes={routes} />
			{fg('magma-hotfix-testing') && <HotfixTesting />}
			{fg('ditto_fcf_jira_globalbackgroundscript') && <ForgeUiGlobalExtensionsAsync />}
		</PageContainer>
	);

	// During M1 of streaming, the client does not need to know that we're streaming, as we're practically returning the same output as with M1 streaming disabled.
	// Because of this only isStreamingAdvisoryEnabled is set to true on the client, but we still need to render the same tree as on SSR.
	// Because of this we check both isStreamingEnabled and isStreamingAdvisoryEnabled to ensure we render the same tree on both.
	if (isStreamingEnabled || isStreamingAdvisoryEnabled) {
		return (
			<>
				<Placeholder name="root">{content}</Placeholder>
				{/* Rendered outside page container to avoid adding any DOM nodes to layout */}
				{isStreamingAdvisoryEnabled && <StreamingAdvisory />}
			</>
		);
	}

	if (fg('jfp-magma-hydration-advisory')) {
		return (
			<>
				{content}
				{/* Rendered outside page container to avoid adding any DOM nodes to layout */}
				<HydrationAdvisory />
			</>
		);
	}
	return content;
};
